import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '../firebase';
import api from '../services/api';

export default function AdminDirectChat({ userId, userName }) {
  const [active, setActive] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/direct-chat/${userId}/messages`);
      setMessages(data.messages || []);
    } catch {}
  }, [userId]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/direct-chat/${userId}/status`);
      setActive(data.active);
    } catch {}
  }, [userId]);

  // Initial load from API
  useEffect(() => {
    loadStatus();
    loadMessages();
  }, [loadStatus, loadMessages]);

  // Listen to RTDB for real-time signal, then fetch from API
  useEffect(() => {
    let unsub;
    try {
      const chatRef = ref(rtdb, `directChats/${userId}`);
      unsub = onValue(chatRef, (snap) => {
        const data = snap.val();
        if (data) {
          setActive(data.active === true);
        }
        loadMessages();
      }, () => {
        // RTDB error — fall back to polling
        console.warn('RTDB unavailable, using polling');
      });
    } catch {
      // RTDB not set up — fall back to polling
    }

    // Polling fallback every 5 seconds
    const poll = setInterval(() => {
      loadStatus();
      loadMessages();
    }, 5000);

    return () => {
      if (unsub) unsub();
      clearInterval(poll);
    };
  }, [userId, loadMessages, loadStatus]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleOpen = async () => {
    setLoading(true); setError('');
    try {
      await api.post(`/direct-chat/${userId}/open`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open chat');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Close this chat? The chat bubble will disappear for the user.')) return;
    setLoading(true); setError('');
    try {
      await api.post(`/direct-chat/${userId}/close`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close chat');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/direct-chat/${userId}/message`, { text });
      setMessages((prev) => [...prev, data]);
      setInput('');
    } catch {
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <div style={s.cardHeaderLeft}>
          <h2 style={s.cardTitle}>Direct Chat</h2>
          <span style={{ ...s.statusDot, background: active ? 'var(--success)' : 'var(--text-3)' }} />
          <span style={s.statusText}>{active ? 'Active' : 'Inactive'}</span>
        </div>
        <button
          onClick={active ? handleClose : handleOpen}
          disabled={loading}
          style={active ? s.closeBtn : s.openBtn}
        >
          {loading ? 'Processing...' : active ? 'End Chat' : 'Start Chat'}
        </button>
      </div>

      {error && <p style={s.error}>{error}</p>}

      {active && (
        <>
          <div style={s.messageArea}>
            {messages.length === 0 && (
              <p style={s.emptyText}>No messages yet. Send the first message to {userName}.</p>
            )}
            {messages.map((msg) => (
              <div key={msg._id || msg.id} style={{ ...s.msgRow, justifyContent: msg.sender === 'admin' ? 'flex-end' : 'flex-start' }}>
                <div style={msg.sender === 'admin' ? s.msgAdmin : s.msgUser}>
                  <p style={s.msgText}>{msg.text}</p>
                  <span style={s.msgTime}>
                    {new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div style={s.inputRow}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Type a message..."
              style={s.input}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              style={{ ...s.sendBtn, opacity: input.trim() && !sending ? 1 : 0.4 }}
            >
              Send
            </button>
          </div>
        </>
      )}

      {!active && messages.length > 0 && (
        <div style={s.messageArea}>
          <p style={s.closedLabel}>Previous conversation</p>
          {messages.map((msg) => (
            <div key={msg._id || msg.id} style={{ ...s.msgRow, justifyContent: msg.sender === 'admin' ? 'flex-end' : 'flex-start' }}>
              <div style={msg.sender === 'admin' ? { ...s.msgAdmin, opacity: 0.6 } : { ...s.msgUser, opacity: 0.6 }}>
                <p style={s.msgText}>{msg.text}</p>
                <span style={s.msgTime}>
                  {new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardHeader: { padding: '16px 20px', borderBottom: '1px solid var(--border-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  cardHeaderLeft: { display: 'flex', alignItems: 'center', gap: '8px' },
  cardTitle: { fontSize: '14px', fontWeight: '700', color: 'var(--navy)' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  statusText: { fontSize: '12px', color: 'var(--text-3)', fontWeight: '600' },
  openBtn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  closeBtn: { background: '#fff', color: 'var(--danger)', border: '1px solid rgba(185,28,28,0.3)', padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  error: { padding: '8px 20px', fontSize: '13px', color: 'var(--danger)', background: 'var(--danger-bg)' },
  emptyText: { textAlign: 'center', color: 'var(--text-3)', fontSize: '13px', padding: '20px' },
  closedLabel: { textAlign: 'center', fontSize: '11px', color: 'var(--text-3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 0 4px' },
  messageArea: { maxHeight: '400px', overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '8px', background: 'var(--bg)' },
  msgRow: { display: 'flex' },
  msgAdmin: { background: 'var(--navy)', color: '#fff', borderRadius: '14px 14px 4px 14px', padding: '10px 14px', maxWidth: '75%' },
  msgUser: { background: '#fff', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', maxWidth: '75%' },
  msgText: { fontSize: '13px', lineHeight: '1.5', margin: 0, wordBreak: 'break-word' },
  msgTime: { fontSize: '10px', opacity: 0.6, marginTop: '4px', display: 'block', textAlign: 'right' },
  inputRow: { display: 'flex', gap: '10px', padding: '12px 20px', borderTop: '1px solid var(--border-2)', background: '#fff' },
  input: { flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none', background: 'var(--bg)' },
  sendBtn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
};
